import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  Box,
  Heading,
  Flex,
  Spinner,
  Text,
  Button,
  Spacer,
  Tooltip
} from '@chakra-ui/react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import { FaCommentDots } from 'react-icons/fa'
import { useImmer } from 'use-immer'

import { toLocaleString, toLocaleString4 } from '@helper/grid/strings'
import generic from '@service/generic'

import {
  IGraphicResponse,
  IGraphicValues
} from '@base/@types/providers/Mrp/PurchaseProvider'

import {
  dailyChart,
  dailySeries,
  montlyChart,
  montlySeries
} from './ParseInfos/parsePeriodChart'

interface MrpPeriodChartProps {
  title: string
  list: any[]
  selected?: any
  setSelected: (value: any) => void
}

enum DaysQuantity {
  month = 30,
  sixMonths = 180,
  year = 365
}

const getDaysQuantity = (timeline: string) => {
  switch (timeline) {
    case 'one_month':
      return DaysQuantity.month
    case 'six_months':
      return DaysQuantity.sixMonths
    case 'one_years':
      return DaysQuantity.year
    default:
      return null
  }
}

const MrpPeriodChart: FC<MrpPeriodChartProps> = ({
  title,
  list,
  selected,
  setSelected
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [selection, setSelection] = useState('one_month')

  const [data, updateData] = useImmer<IGraphicResponse>({
    storage: [],
    sales: []
  })
  const [dataMontlhy, updateDataMontlhy] = useImmer<IGraphicResponse>({
    storage: [],
    sales: []
  })

  const [filteredData, setFilteredData] = useImmer<IGraphicResponse>({
    storage: [],
    sales: []
  })

  const item = useMemo(() => {
    if (selected) {
      const item = list.find(
        (e: any) =>
          e.estoque.produtoFornecedor.produto.id === selected['produto-id']
      )
      return item
    }

    return null
  }, [list, selected])

  const handleModalClose = () => {
    setSelected(null)
  }

  const updateDataGraph = (timeline: string) => {
    setSelection(timeline)
    const daysQuantity = getDaysQuantity(timeline)

    const dateNow = new Date()
    const oldDate = new Date().setDate(dateNow.getDate() - daysQuantity)
    const currentDate = new Date().setDate(dateNow.getDate())
    const chartData = {
      storage: data.storage.filter(item => {
        const itemDate = new Date(item.data)

        return itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
      }),
      sales: data.sales.filter(item => {
        const itemDate = new Date(item.data)

        return itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
      })
    }

    setFilteredData(timeline === 'all' ? data : chartData)
    updateDataMontlhy(() => {
      const newSales: { data: any; descricao: any; valor: any }[] = []
      const monthsAndYears = data.sales.map((obj: any) => {
        return {
          month: new Date(obj.data).getMonth(),
          year: new Date(obj.data).getFullYear()
        }
      })
      const uniqueMonthsAndYears = monthsAndYears.filter(
        (monthAndYear: { month: any; year: any }, index: any, self: any[]) =>
          self.findIndex(
            t => t.month === monthAndYear.month && t.year === monthAndYear.year
          ) === index
      )
      try {
        uniqueMonthsAndYears.forEach((obj: any) => {
          const salesAux = data.sales.filter((dataPoint: any) => {
            // Get the month from the date property of the data point
            const dataPointMonth = new Date(dataPoint.data).getMonth()
            const dataPointYear = new Date(dataPoint.data).getFullYear()
            // Compare the data point month to the month passed in as a parameter
            return dataPointMonth === obj.month && dataPointYear === obj.year
          })
          if (salesAux.length > 0) {
            const newSale = {
              data: salesAux[0].data,
              descricao: data.sales[0].descricao,
              valor: salesAux
                .map((item: any) => item.valor)
                .reduce((prev: any, curr: any) => prev + curr, 0)
            }
            newSales.push(newSale)
          }
        })
        return {
          storage: data.storage,
          sales: newSales
        }
      } catch (e) {}
    })
  }

  const getChartData = useCallback(async () => {
    setLoading(true)
    setError(false)

    try {
      const [storageResult, salesResult] = await Promise.allSettled([
        generic.getStorageChart(selected['centro-id'], selected['produto-id']),
        generic.getSalesChart(selected['centro-id'], selected['produto-id'])
      ])

      const storage: any[] =
        storageResult.status === 'fulfilled'
          ? storageResult.value?.data?.data ?? []
          : []

      const rawSales: any[] =
        salesResult.status === 'fulfilled'
          ? salesResult.value?.data?.data ?? []
          : []

      const datasUpdates: any = []
      storage.map((x: any) => {
        const obj = rawSales.find(
          (objeto: { data: any }) => objeto.data === x.data
        )
        if (obj) {
          datasUpdates.push(obj)
        } else {
          datasUpdates.push({
            data: x.data,
            descricao: 'CD Barueri',
            valor: 0
          })
        }
      })

      // Se storage veio vazio mas sales veio com dados, preenche datasUpdates com sales
      if (storage.length === 0 && rawSales.length > 0) {
        rawSales.forEach((s: any) =>
          datasUpdates.push({ ...s, valor: s.valor ?? 0 })
        )
      }

      updateData(draft => {
        draft.storage = storage
        draft.sales = datasUpdates
      })

      setFilteredData(() => {
        setSelection('one_month')

        const daysQuantity = getDaysQuantity('one_month')

        const dateNow = new Date()
        const oldDate = new Date().setDate(dateNow.getDate() - daysQuantity)
        const currentDate = new Date().setDate(dateNow.getDate())

        return {
          storage: storage.filter((item: IGraphicValues) => {
            const itemDate = new Date(item.data)
            return (
              itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
            )
          }),
          sales: datasUpdates.filter((item: IGraphicValues) => {
            const itemDate = new Date(item.data)
            return (
              itemDate > new Date(oldDate) && itemDate <= new Date(currentDate)
            )
          })
        }
      })

      updateDataMontlhy(() => {
        const newSales: { data: any; descricao: any; valor: any }[] = []

        const monthsAndYears = datasUpdates.map((obj: any) => {
          return {
            month: new Date(obj.data).getMonth(),
            year: new Date(obj.data).getFullYear()
          }
        })
        const uniqueMonthsAndYears = monthsAndYears.filter(
          (monthAndYear: { month: any; year: any }, index: any, self: any[]) =>
            self.findIndex(
              t =>
                t.month === monthAndYear.month && t.year === monthAndYear.year
            ) === index
        )
        try {
          uniqueMonthsAndYears.forEach((obj: any) => {
            const salesAux = datasUpdates.filter((dataPoint: any) => {
              const dataPointMonth = new Date(dataPoint.data).getMonth()
              const dataPointYear = new Date(dataPoint.data).getFullYear()
              return dataPointMonth === obj.month && dataPointYear === obj.year
            })
            if (salesAux.length > 0) {
              const newSale = {
                data: salesAux[0].data,
                descricao: datasUpdates[0]?.descricao ?? '',
                valor: salesAux
                  .map((item: any) => item.valor ?? 0)
                  .reduce((prev: any, curr: any) => prev + curr, 0)
              }
              newSales.push(newSale)
            }
          })
          return {
            storage: storage,
            sales: newSales
          }
        } catch (e) {}
      })
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [selected, setFilteredData, updateData, updateDataMontlhy])
  useEffect(() => {
    if (selected) {
      getChartData()
    }
  }, [selected, getChartData])

  return (
    <Modal
      isOpen={!!selected}
      onClose={handleModalClose}
      motionPreset="slideInBottom"
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderTopRadius={5} bgColor="white">
          {selected
            ? selected['produto-id'] + ' - ' + selected['produto-material']
            : 'Graficos'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody borderBottomRadius={5} bgColor="white" pb={6}>
          <Tabs colorScheme="green" variant="soft-rounded" isFitted>
            <TabList>
              <Tab _focus={{ boxShadow: 'none' }}>Diário</Tab>
              <Tab _focus={{ boxShadow: 'none' }}>Mensal</Tab>
            </TabList>

            {loading ? (
              <Flex alignItems="center" justifyContent="center" h={240}>
                <Spinner />
              </Flex>
            ) : error ? (
              <Flex alignItems="center" justifyContent="center" h="120px">
                <Text>Dados insuficientes para o gráfico</Text>
              </Flex>
            ) : (
              <TabPanels>
                <TabPanel>
                  <Flex marginBottom={2}>
                    <Button
                      id="one_month"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      onClick={() => updateDataGraph('one_month')}
                      borderColor="green"
                      backgroundColor={
                        selection === 'one_month' ? 'green.100' : 'gray.100'
                      }
                    >
                      1M
                    </Button>

                    <Button
                      id="six_months"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      transition="all ease .3s"
                      onClick={() => updateDataGraph('six_months')}
                      backgroundColor={
                        selection === 'six_months' ? 'green.100' : 'gray.100'
                      }
                    >
                      6M
                    </Button>

                    <Button
                      id="one_month"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      transition="all ease .3s"
                      onClick={() => updateDataGraph('one_years')}
                      backgroundColor={
                        selection === 'one_years' ? 'green.100' : 'gray.100'
                      }
                    >
                      1Y
                    </Button>

                    <Button
                      id="all_months"
                      marginRight={2}
                      fontSize={14}
                      size="sm"
                      transition="all ease .3s"
                      onClick={() => updateDataGraph('all')}
                      backgroundColor={
                        selection === 'all' ? 'green.100' : 'gray.100'
                      }
                    >
                      ALL
                    </Button>
                  </Flex>

                  <Chart
                    options={dailyChart(filteredData, title)}
                    series={dailySeries(filteredData)}
                    width="100%"
                    height="240px"
                  />
                </TabPanel>

                <TabPanel>
                  <Chart
                    options={montlyChart(dataMontlhy, title)}
                    series={montlySeries(dataMontlhy)}
                    width="100%"
                    height="240px"
                  />
                </TabPanel>
              </TabPanels>
            )}
          </Tabs>

          <Box mb={4}>
            <Heading size="md" mb={2}>
              Estoque
            </Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Reservado
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.quantidadeReservada : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Dep. 13
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.estoquesIndisponiveis.Luizinho : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Dep. 16
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.estoquesIndisponiveis.Deposito16 : '-/-'}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box mb={4}>
            <Heading size="md" mb={2}>
              Parâmetros
            </Heading>
            <Grid templateColumns={'repeat(5, 1fr)'} gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Frequência
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.periodoRevisao : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Lead Time
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.tempoDeEspera : '-/-'}
                </Text>
              </Box>
              {/* <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Estoque Arbitrado.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.quantidadeArbitraria : '-/-'}
                </Text>
              </Box> */}
              <>
                <Box p={4} bg="gray.100" borderRadius="lg">
                  <Heading
                    size="xs"
                    fontWeight="light"
                    mb={1}
                    maxW="100%"
                    isTruncated
                  >
                    {item?.estoque?.parametroIncrementoEstoqueMRP
                      ? item.estoque.parametroIncrementoEstoqueMRP
                          .tipoParametro === 0
                        ? 'Increm. Estq Dias'
                        : 'Increm. Estq Qtd'
                      : 'Sem Incremento'}
                  </Heading>
                  <Flex>
                    <Text fontSize="2xl" lineHeight="1.2">
                      {item?.estoque?.parametroIncrementoEstoqueMRP
                        ? item.estoque.parametroIncrementoEstoqueMRP
                            .tipoParametro === 0
                          ? item.estoque.parametroIncrementoEstoqueMRP
                              .estoqueDias
                          : item.estoque.parametroIncrementoEstoqueMRP
                              .incrementoEstoque
                        : '0'}
                    </Text>
                    <Spacer />
                    {item?.estoque?.parametroIncrementoEstoqueMRP
                      ?.observacao && (
                      <>
                        <Box pt={2}>
                          <Tooltip
                            label={
                              item?.estoque?.parametroIncrementoEstoqueMRP
                                ?.observacao
                            }
                            placement="top"
                            hasArrow
                          >
                            <span>
                              <FaCommentDots />
                            </span>
                          </Tooltip>
                        </Box>
                      </>
                    )}
                  </Flex>
                </Box>
              </>

              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Lote Mult.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.produtoFornecedor.quantidadePacote : ''}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Politica de Est.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.estoqueSegurancaInformadoEmDias : ''}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box mb={4}>
            <Heading size="md" mb={2}>
              Previsões
            </Heading>
            <Grid templateColumns="repeat(5, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Período
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.previsaoDemanda.periodoCoberturaEmDias : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Saída Projetada
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? toLocaleString4(
                        item.previsaoDemanda.quantidadeAcimaPeriodoCobertura
                      )
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Transf. Projetada
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? item.estoque.loja.centroDistribuicao ||
                      item.estoque.loja.lojaCentroDistribuicao
                      ? toLocaleString4(item.remessas)
                      : '-/-'
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Estoque Seguranca.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? toLocaleString4(
                        item.previsaoDemanda.previsaoEstoqueSeguranca
                      )
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Eventos/Dias Estoque.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item ? item.estoque.diasComEstoque : ''}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box>
            <Heading size="md" mb={2}>
              Outros
            </Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap="24px">
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Fornecedor
                </Heading>
                <Text fontSize="2xl" maxW="160px" isTruncated lineHeight="1.2">
                  {item
                    ? item.estoque.produtoFornecedor.unidadeFornecedor
                      ? item.estoque.produtoFornecedor.unidadeFornecedor
                          .fornecedorNome
                      : '-/-'
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Fator Conv.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  {item
                    ? item.estoque.produtoFornecedor.unidadeFornecedor
                      ? item.estoque.produtoFornecedor.unidadeFornecedor
                          .fatorConversao
                      : '-/-'
                    : '-/-'}
                </Text>
              </Box>
              <Box p={4} bg="gray.100" borderRadius="lg">
                <Heading
                  size="xs"
                  fontWeight="light"
                  mb={1}
                  maxW="100%"
                  isTruncated
                >
                  Custo Unit.
                </Heading>
                <Text fontSize="2xl" lineHeight="1.2">
                  R${' '}
                  {item
                    ? toLocaleString(item.estoque.custoUnitarioFatorConversao)
                    : '-/-'}
                </Text>
              </Box>
            </Grid>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default MrpPeriodChart
